import { useGetUsers } from '@/src/api/users';
import { useMemo } from 'react'
import { MediaBuyerOption } from '../interfaces';
import { IUser } from '@clickster/constant-definitions/dist-esm/entities/user/types';
import { ITrafficSource } from '@clickster/constant-definitions/dist-esm/entities/traffic-source/types';
import { IRotation } from '@clickster/constant-definitions/dist-esm/entities/rotation/types';
import { useGetGroups } from '@/src/api/groups';
import { useGetTrafficSource } from '@/src/api/traffic';
import { useGetCampaigns } from '@/src/api/campaigns';
import { useGetAffiliatesNetwork } from '@/src/api/affiliates';
import { useGetOffers } from '@/src/api/offers';
import { useGetRotations } from '@/src/api/rotations';
import { ISelectOption } from '@/src/components/ui/multiselects/multi-select-filter-counter';
import { arrayToObjectIndexed } from '@/src/utils/objects';

interface UseGetFilterStateOptionsProps {
    disableCampaigns?: boolean;
    disableMediaBuyers?: boolean;
    disableTrafficSources?: boolean;
    disableCategories?: boolean;
    disableAffiliates?: boolean;
    disableOffers?: boolean;
    disableRotations?: boolean;
}

const useGetFilterStateOptions = ({
    disableCampaigns,
    disableMediaBuyers,
    disableTrafficSources,
    disableCategories,
    disableAffiliates,
    disableOffers,
    disableRotations,
}: UseGetFilterStateOptionsProps = {}) => {
    /* HOOKS */
    const { groups: campaignGroupsData = [], isLoading: isCampaignGroupsLoading } = useGetGroups({
        enabled: !disableCategories
    })

    const { users: mediaBuyersData = [], isLoading: isMediaBuyersLoading } = useGetUsers({
        customKey: 'drilldown-media-buyers',
        params: {
            select: '_id firstName lastName status jobTitle'
        },
        enabled: !disableMediaBuyers
    })

    const { trafficSources: trafficSourcesData = [], isLoading: isTrafficSourcesLoading } = useGetTrafficSource({
        customKey: 'drilldown-traffic-sources',
        params: {
            select: '_id name status'
        },
        enabled: !disableTrafficSources
    })

    const { campaigns: sourceCampaignsData = [], isLoading: isFetchingCampaigns, refetch: refetchCampaigns, } = useGetCampaigns({
        customKey: 'drilldown-campaigns',
        params: {
            select: 'nickname _id name group consecutive_in_traffic_source trafficSource.api status rotation media_buyer',
            populate: ['trafficSource', 'rotation'],
        },
        enabled: !disableCampaigns
    })

    const { affiliates: affiliatesData = [], isLoading: isAffiliatesLoading } = useGetAffiliatesNetwork({
        customKey: 'drilldown-affiliates',
        params: {
            select: 'name api_configuration _id platform status',
            populate: ['trafficSource'],
        },
        enabled: !disableAffiliates
    })

    const { offers: offersData = [], isLoading: isOffersLoading } = useGetOffers({
        customKey: 'drilldown-offers',
        params: {
            select: '_id name status affiliate',
        },
        enabled: !disableOffers
    });

    const { rotations: rotationsData = [], isLoading: isRotationsLoading } = useGetRotations({
        customKey: 'drilldown-rotations',
        queryName: 'rotations-_id name',
        params: {
            select: '_id name archiveStatus category group',
            sortField: 'name',
            sortOrder: 'asc',
        },
        enabled: !disableRotations
    })

    /* MEMOS */
    const campaignsOptions = useMemo<ISelectOption[]>(() =>
        sourceCampaignsData.filter(campaign => campaign.trafficSource && typeof campaign.trafficSource === 'object').map(campaign => {
            const trafficSource = (campaign.trafficSource as ITrafficSource)
            return {
                _id: campaign._id as string,
                value: campaign._id as string,
                label: campaign.name as string,
                name: campaign.name as string,
                status: campaign.status === "active" ? "active" : "inactive",
                rotation: campaign.rotation,
                trafficSource: {
                    _id: trafficSource?._id,
                    name: trafficSource?.name,
                    status: trafficSource?.status,
                    shortname: trafficSource?.shortname
                },
                logo: trafficSource?.logo,
                consecutive_in_traffic_source: campaign.consecutive_in_traffic_source,
                media_buyer: campaign?.media_buyer
            }
        }) || [], [sourceCampaignsData]);

    const campaignGroupsOptions = useMemo<ISelectOption[]>(
        () =>
            campaignGroupsData
                .filter((g) => g.object_name === 'campaign' && g._id) // Filter out items without _id
                .map((group) => ({
                    label: group.name,
                    value: group._id as string,
                    _id: group._id as string,
                    status: (group as any).archiveStatus || (group as any).status || 'active'
                })),
        [campaignGroupsData],
    );

    const trafficSourcesOptions = useMemo<ISelectOption[]>(
        () =>
            trafficSourcesData.map((trafficSource: ITrafficSource) => ({
                value: trafficSource._id as string,
                _id: trafficSource._id as string,
                label: trafficSource.name,
                logo: trafficSource.logo || '',
                status: trafficSource.status
            })) as ISelectOption[],
        [trafficSourcesData],
    );

    const mediaBuyersOptions = useMemo<MediaBuyerOption[]>(
        () =>
            mediaBuyersData
                .filter((mb) => mb.jobTitle === 'Media Buyer' && mb._id)
                .map((user: IUser) => ({
                    _id: user._id!,
                    index: user._id!, // Using _id as string index
                    label: `${user.firstName} ${user.lastName}`,
                    value: user._id!,
                    jobTitle: user.jobTitle!,
                    isDisabled: false
                })),
        [mediaBuyersData],
    );

    const rotationGroupsOptions = useMemo<ISelectOption[]>(() => {
        // Extract unique groups from rotations
        const groupsMap = new Map();
        rotationsData?.forEach((rotation: IRotation) => {
            const group = (rotation as any).group;
            if (group && group._id && group.object_name === 'rotation') {
                if (!groupsMap.has(group._id)) {
                    groupsMap.set(group._id, {
                        _id: group._id as string,
                        name: group.name as string,
                        value: group._id as string,
                        label: group.name as string,
                        status: (group as any).archiveStatus || (group as any).status || 'active'
                    });
                }
            }
        });
        return Array.from(groupsMap.values());
    }, [rotationsData]);

    const rotationsOptions = useMemo<ISelectOption[]>(() =>
        rotationsData?.map((item: IRotation) => ({
            _id: item._id as string,
            name: item.name as string,
            status: item.archiveStatus as string,
            value: item._id as string,
            label: item.name as string,
            id_relation: item.group?._id || undefined
        })) || []
        , [rotationsData]);




    const affiliatesOptions = useMemo(
        () =>
            affiliatesData
                .filter((a) => a?.name && a._id)
                .map((affiliate: any) => ({
                    label: affiliate.name,
                    value: affiliate._id,
                    _id: affiliate._id,
                    status: affiliate.status
                })),
        [affiliatesData],
    );

    const offersOptions = useMemo(
        () =>
            offersData
                .filter((offer) => offer?.name && offer._id)
                .map((offer: any) => ({
                    label: offer.name,
                    value: offer._id,
                    _id: offer._id,
                    status: offer?.status,
                    affiliate: {
                        _id: offer?.affiliate?._id
                    }
                })),
        [offersData],
    );

    const campaignsIndexed = useMemo(() => arrayToObjectIndexed(campaignsOptions, '_id'), [campaignsOptions])

    return {
        data: {
            mediaBuyers: mediaBuyersData,
            campaigns: sourceCampaignsData,
            rotations: rotationsData,
            affiliates: affiliatesData,
            campaignGroups: campaignGroupsData,
            trafficSources: trafficSourcesData,
            categories: rotationsData,
            offers: offersData,
        },
        options: {
            mediaBuyers: mediaBuyersOptions,
            campaigns: campaignsOptions,
            rotations: rotationsOptions,
            rotationGroups: rotationGroupsOptions,
            affiliates: affiliatesOptions,
            campaignGroups: campaignGroupsOptions,
            trafficSources: trafficSourcesOptions,
            categories: rotationsOptions,
            offers: offersOptions
        },
        loaders: {
            mediaBuyers: isMediaBuyersLoading,
            campaigns: isFetchingCampaigns,
            affiliates: isAffiliatesLoading,
            campaignGroups: isCampaignGroupsLoading,
            trafficSources: isTrafficSourcesLoading,
            categories: isRotationsLoading,
            offers: isOffersLoading
        },
        indexed: {
            campaigns: campaignsIndexed
        }
    }
}

export default useGetFilterStateOptions

export type UseGetFilterStateOptionsType = ReturnType<typeof useGetFilterStateOptions>
